/**
 * SOLID Review
 * S — Strong. One concern: the Application's lollipop for the Framework — every use case React may
 *     trigger, as a runtime contract.
 * O — Good. A new use case is a new row; the type and the committed document follow from it.
 * L — N/A.
 * I — Good. One port by design, like the backend's `UseCases`; a component narrows it with `Pick<>`.
 * D — Good. The kernel's zod adapter and the Domain's command schemas.
 */
import { z } from 'zod';
import { zodPortContract, type PortInterface, type IConfigZodPortContract } from '@ml3d/kernel/zod';
import { ContextRequest } from '../../domain/ports/schemas/ContextRequest';
import { ResultOf, ResultVoid } from '../../domain/ports/schemas/ResultSchema';
import { CommandNothing, CommandAnnounce, CommandLeave } from '../../domain/ports/schemas/TableMoves';
import { CommandStartSession, CommandCreateGuest, CommandTableCode, CommandNavigate } from '../../domain/ports/schemas/SessionMoves';
import {
  CommandEmail, CommandSignUp, CommandVerifyCode, CommandReferral, CommandNickname, CommandEmailCode,
  PayloadReferral, PayloadProfile, RequestEmail,
} from '../../domain/ports/schemas/AuthMoves';
import { PayloadLegalDocuments, DtoLegalReview, CommandLegalAcceptance, PayloadDataExport, CommandDeleteAccount } from '../../domain/ports/schemas/LegalMoves';
import { CommandCookiePreferences, DtoPreferencesOpened } from '../../domain/ports/schemas/ConsentMoves';
import { DtoCheckout, DtoBalance } from '../../domain/ports/schemas/PaymentMoves';
import { DtoHealthReport } from '../../domain/ports/schemas/HealthMoves';
import { CommandClientError } from '../../domain/ports/schemas/DiagnosticsMoves';
import {
  CommandMediaClock, CommandMediaIdle, CommandMediaBanner, CommandMediaUsed, RequestMediaToken, DtoMediaToken, RequestMediaConnection,
  RequestMediaHealth, DtoMediaHealth, RequestMediaUsage, DtoMediaUsage,
} from '../../domain/ports/schemas/MediaMoves';

/**
 * WHAT REACT MAY ASK OF THE APPLICATION (frontend strict-ports plan, FE1, 2026-10-03) — the client twin
 * of the backend's `UseCases`. Every op is `(ctx, command) → Result<void>`. React never reads an answer
 * from here: what a use case did lands in the table, and React reads the table through
 * `ClientStateView`. A failure carries `{ code, message }`, which is what a component shows, such as
 * the lobby's start error, or ignores, as a refused tap.
 *
 * FE1 holds the table loop. Each later phase adds its context's rows (session, auth, legal, payment,
 * ads, media).
 */
const op = <C extends z.ZodTypeAny>(command: C) => ({ args: z.tuple([ContextRequest, command]), result: ResultVoid });
/** An op whose answer the screen shows (FE3): the sign-up switch, the referral's verdict, the address a code went to. */
const answering = <C extends z.ZodTypeAny, A extends z.ZodTypeAny>(command: C, answer: A) => ({
  args: z.tuple([ContextRequest, command]),
  result: ResultOf(answer),
});

const ClientUseCasesOps = {
  // ── the local player's moves ──────────────────────────────────────────────────────────────────
  startGame: op(CommandNothing),
  rollDice: op(CommandNothing),
  peekDice: op(CommandNothing),
  announce: op(CommandAnnounce),
  believe: op(CommandNothing),
  liftCup: op(CommandNothing),
  rollFirst: op(CommandNothing),
  playAgain: op(CommandNothing),
  leaveTable: op(CommandLeave),
  // ── keeping the table level with the server ───────────────────────────────────────────────────
  // (No `resync`: React never asked for one. The Application catches up by itself when the connection
  // comes back after a gap — the `connectivity` event's answer — and the op went in FE6c, §29.4b.)
  /** The lobby's roster read on entry. */
  loadLobbyRoom: op(CommandNothing),
  /** One tick of the lobby's self-heal poll — `ClientJobs` calls it every 2.5 s in the lobby. */
  lobbyPollTick: op(CommandNothing),
  /** Watch the server's streams for the table as it stands now. Idempotent. */
  watchTable: op(CommandNothing),
  unwatchTable: op(CommandNothing),
  // ── the session (FE2) ─────────────────────────────────────────────────────────────────────────
  /** The page loaded: hydrate's identity and screen, and what the URL asked for. */
  startSession: op(CommandStartSession),
  /** The game screen mounted: identity, the stored game, then the code, the cold rejoin, the host — or back. */
  enterGame: op(CommandNothing),
  /** The name modal's submit: create the guest, then go on with the entry. */
  createGuest: op(CommandCreateGuest),
  /** EnterCodeScreen's Continue. */
  checkTableCode: op(CommandTableCode),
  /** A funnel button. */
  navigate: op(CommandNavigate),
  /** The welcome screen's ✕ on a join error. */
  dismissJoinError: op(CommandNothing),
  // ── the account (FE3) ─────────────────────────────────────────────────────────────────────────
  /** The email screen in sign-in mode: ask for a code. The answer is the same for every address (Q10, §28.248) — on to the code screen. */
  requestLoginCode: op(CommandEmail),
  /** The email screen in sign-up mode. */
  signUp: op(CommandSignUp),
  /** The code screen: a code accepted signs the tab in. */
  verifyCode: op(CommandVerifyCode),
  /** The referral field's check, on blur. */
  checkReferral: answering(CommandReferral, PayloadReferral),
  /** The profile opened: the account as the server has it — null when there is none to read. */
  loadProfile: answering(CommandNothing, PayloadProfile.nullable()),
  changeNickname: op(CommandNickname),
  /** The profile's "send code" for a new address; answers the address it went to. */
  requestEmailChange: answering(CommandEmail, RequestEmail),
  verifyEmailChange: op(CommandEmailCode),
  /** Log out — the welcome screen's and the profile's. */
  signOut: op(CommandNothing),
  // ── the legal side (FE4a) ─────────────────────────────────────────────────────────────────────
  /** The legal modal's tabs: every current document. */
  loadLegalDocuments: answering(CommandNothing, PayloadLegalDocuments),
  /** The re-acceptance gate: the documents a signed-in player must accept again, or none (fails open). */
  checkLegalReview: answering(CommandNothing, DtoLegalReview),
  /** The gate's Accept: one consent per reviewed document. */
  acceptLegalTerms: op(CommandLegalAcceptance),
  /** GDPR Art. 20: the player's data, for the screen to download. */
  exportMyData: answering(CommandNothing, PayloadDataExport),
  /** GDPR Art. 17, step one: a verification code by email. */
  requestAccountDeletion: op(CommandNothing),
  /** GDPR Art. 17, step two: the account deleted with the code — and the tab signed out. */
  deleteAccount: op(CommandDeleteAccount),
  // ── cookie consent (FE4b) ─────────────────────────────────────────────────────────────────────
  /** Boot the consent platform — once, however often the provider mounts. */
  startConsent: op(CommandNothing),
  /** "Cookie settings": the certified CMP's own UI if it can show one — `opened: false` asks for the modal. */
  openCookiePreferences: answering(CommandNothing, DtoPreferencesOpened),
  /** The first-party modal's Save. */
  saveCookiePreferences: op(CommandCookiePreferences),
  // ── the account's credits (FE4c) ──────────────────────────────────────────────────────────────
  /** "Buy": Stripe's checkout for a credits pack — where the browser goes next. */
  startCheckout: answering(CommandNothing, DtoCheckout),
  /** After a purchase: the account's balance, read once and kept — retrying is the screen's. */
  readBalance: answering(CommandNothing, DtoBalance),
  /** Hear the seated player's pushed balance; idempotent, so StrictMode's double effect subscribes once. */
  watchAccount: op(CommandNothing),
  unwatchAccount: op(CommandNothing),
  // ── the ad slot (FE4d) ────────────────────────────────────────────────────────────────────────
  /** Ask for the seated free player's ad provider — once per player; a premium player never asks. */
  loadAdConfig: op(CommandNothing),
  // ── the server's health (FE5a) ────────────────────────────────────────────────────────────────
  /** One /health probe, for the maintenance banner and the health indicator. */
  checkServerHealth: answering(CommandNothing, DtoHealthReport),
  // ── the table's video chat (FE5b/FE5c) ────────────────────────────────────────────────────────
  /** The 📡 control: on or off at this table. */
  toggleMedia: op(CommandMediaClock),
  /** The idle banner's or badge's Reconnect. */
  reconnectMedia: op(CommandMediaClock),
  /** One tick of the idle check (§23.7) — `ClientJobs` runs it while video is on at a table. */
  checkMediaIdle: op(CommandMediaIdle),
  /** The server's idle sweep removed the player from the media room (§21.10). */
  mediaRemovedByServer: op(CommandNothing),
  dismissMediaBanner: op(CommandMediaBanner),
  /** The microphone or the camera came on — reported once per kind per room, never for a guest. */
  mediaUsed: op(CommandMediaUsed),
  /** A LiveKit token for the room and player the SDK is connecting. */
  mediaToken: answering(RequestMediaToken, DtoMediaToken),
  /** The SDK connected or disconnected — for the room it was in. */
  reportMediaConnection: op(RequestMediaConnection),
  /** The admin's LiveKit health line. */
  checkMediaHealth: answering(RequestMediaHealth, DtoMediaHealth),
  /** The admin's usage report. */
  loadMediaUsageReport: answering(RequestMediaUsage, DtoMediaUsage),
  // ── client error reporting (2026-10-10) ────────────────────────────────────────────────────────
  /** An error the Framework caught — a window error, a rejection, a render, a failed port call. */
  reportClientError: op(CommandClientError),
} as const;
const clientUseCasesConfig: IConfigZodPortContract<typeof ClientUseCasesOps> = { name: 'ClientUseCases', ops: ClientUseCasesOps };
export const ClientUseCasesContract = zodPortContract(clientUseCasesConfig);

export type IPortClientUseCases = PortInterface<typeof ClientUseCasesOps>;

/** The command an op takes, named by the op — how a component declares one, as the backend's request
 *  adapters name theirs (`TUseCaseInput`). */
export type TClientUseCaseInput<K extends keyof IPortClientUseCases> = Parameters<IPortClientUseCases[K]>[1];
/** What op K answers when it succeeds — a component names an answer by its op, as it names a command (FE7). */
export type TClientUseCaseOutput<K extends keyof IPortClientUseCases> =
  Extract<Awaited<ReturnType<IPortClientUseCases[K]>>, { success: true }>['value'];
